import { useEffect, useState } from "react";

import { RoleTypeEnum } from "@superblocksteam/shared";
import type {
  OrganizationUserDto,
  Pagination,
  RoleDto,
} from "@superblocksteam/shared";

import { callServer, HttpMethod } from "store/utils/client";

// Preferred first: an Owner, then an Admin.
const CONTACT_ROLE_NAMES = ["Owner", "Admin"] as const;

/**
 * Email of an active org Owner (else Admin), or null if there is none.
 * Reads the role list plus one `limit=1` users page per role, never the
 * whole org.
 */
export async function fetchOrgAdminEmail(
  organizationId: string,
): Promise<string | null> {
  const roles = await callServer<RoleDto[]>(
    {
      method: HttpMethod.Get,
      url: `v1/organizations/${organizationId}/roles`,
      query: { type: RoleTypeEnum.ORGANIZATION },
    },
    { notifyOnError: false, shouldThrowHttpError: true },
  );
  for (const roleName of CONTACT_ROLE_NAMES) {
    const role = roles?.find((r) => r.name === roleName);
    if (!role) continue;
    const usersPage = await callServer<Pagination<OrganizationUserDto>>(
      {
        method: HttpMethod.Get,
        url: `v1/organizations/${organizationId}/users`,
        query: { limit: "1", offset: "0", status: "active", roleId: role.id },
      },
      { notifyOnError: false, shouldThrowHttpError: true },
    );
    const email = usersPage?.items?.[0]?.email;
    if (email) return email;
  }
  return null;
}

export type OrgAdminContact =
  | { status: "idle" | "loading" | "none" }
  | { status: "found"; email: string }
  | { status: "error"; forbidden: boolean };

/**
 * Resolves the admin contact while `enabled`, so a "Contact admin" click can
 * open its mailto link synchronously inside the user gesture.
 */
export function useOrgAdminContact(
  organizationId: string | null | undefined,
  enabled: boolean,
): OrgAdminContact {
  const [contact, setContact] = useState<OrgAdminContact>({ status: "idle" });
  useEffect(() => {
    if (!enabled || !organizationId) return;
    let cancelled = false;
    setContact({ status: "loading" });
    fetchOrgAdminEmail(organizationId)
      .then((email) => {
        if (cancelled) return;
        setContact(email ? { status: "found", email } : { status: "none" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code =
          err && typeof err === "object" && "code" in err
            ? (err as { code: unknown }).code
            : null;
        setContact({ status: "error", forbidden: code === 403 });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, organizationId]);
  // Without an org there is nothing to look up; report that instead of idling.
  if (enabled && !organizationId) return { status: "error", forbidden: false };
  return contact;
}
